/**
 * Zod validation helpers for TanStack Query
 *
 * Эти утилиты позволяют валидировать ответы API через zod схемы,
 * сгенерированные Orval, обеспечивая runtime типобезопасность.
 */

import * as Sentry from "@sentry/react"
import type { ZodType } from "zod"

/**
 * Валидирует данные из успешного ответа API
 *
 * @param schema - Zod схема для валидации
 * @param response - Ответ от API (может быть undefined, null или union типом)
 * @param options.status - Ожидаемый HTTP status (по умолчанию 200). Можно передать массив.
 * @returns Валидированные данные или null при ошибке/неуспешном ответе
 *
 * @example
 * ```typescript
 * const { data } = useAnalyticsGetGeneralAppointmentsData(params)
 * const validated = validateSuccessResponse(
 *   getGeneralAppointmentsDataResponse,
 *   data
 * )
 *
 * if (validated) {
 *   // validated имеет тип GeneralAppointmentsResponse
 *   console.log(validated.commonStat)
 * }
 * ```
 */
export function validateSuccessResponse<T>(
	schema: ZodType<T>,
	response: unknown,
	options?: { status?: number | readonly number[] },
): T | null {
	// Проверяем, что response имеет ожидаемую структуру
	if (
		!response ||
		typeof response !== "object" ||
		!("status" in response) ||
		!("data" in response)
	) {
		return null
	}

	const responseWithStatus = response as { status: number; data: unknown }
	const successStatuses = options?.status
		? Array.isArray(options.status)
			? options.status
			: [options.status]
		: [200]

	if (!successStatuses.includes(responseWithStatus.status)) {
		return null
	}

	const result = schema.safeParse(responseWithStatus.data)

	if (!result.success) {
		// Логируем ошибки валидации в Sentry
		Sentry.captureException(result.error, {
			tags: { category: "validation", type: "zod" },
			extra: {
				issues: result.error.issues,
			},
		})

		if (process.env.NODE_ENV === "development") {
			console.error("Zod validation error:", result.error.issues)
		}

		return null
	}

	return result.data
}
